import React from 'react';
import { Link } from 'react-router-dom';
import './AccessibilityStatement.css';

const accessibilityFeatures = [
  'Keyboard navigation support throughout the application',
  'Screen reader compatibility with proper ARIA labels',
  'High contrast mode for better visibility',
  'Adjustable font sizes from 12px to 24px',
  'Reduced motion options for users sensitive to animations',
  'Alternative text for images and visual elements',
  'Visible focus indicators for keyboard navigation',
  'Semantic HTML structure for assistive technology support'
];

const knownLimitations = [
  'Some complex 3D visualizations may not be fully accessible to screen readers.',
  'Video content may not have complete captions in every case.',
  'Some interactive analysis dashboard elements may still require mouse input.'
];

const supportChannels = [
  'Phone: +1 (555) 123-4567',
  'Address: 123 Technology Drive, Innovation City, IC 12345'
];

const technicalSpecifications = ['HTML', 'WAI-ARIA', 'CSS', 'JavaScript'];

const assessmentApproach = [
  'Self-evaluation using automated accessibility testing tools',
  'Manual testing with screen readers including NVDA, JAWS, and VoiceOver',
  'Keyboard-only navigation testing',
  'User testing with people with disabilities'
];

const AccessibilityStatement = () => {
  const lastUpdated = new Date().toLocaleDateString();

  return (
    <main className="accessibility-statement" role="main">
      <div className="statement-shell">
        <div className="statement-container">
          <header className="statement-header">
            <Link
              to="/"
              className="statement-back-link"
              aria-label="Go back to the main page"
            >
              <i className="fa-solid fa-angle-right statement-back-icon" aria-hidden="true" />
            </Link>

            <p className="statement-eyebrow">123bimlive by RDV Systems</p>
            <h1>Accessibility Statement</h1>
            <p className="statement-intro">
              We are committed to making <strong>123bimlive.com</strong> accessible for people with disabilities and
              continually improving the experience across browsing, analysis, and visualization
              workflows.
            </p>

            <div className="statement-meta" aria-label="Accessibility statement summary">
              <span className="statement-badge">WCAG 2.1 target</span>
              <span className="statement-badge statement-badge-muted">Partially conformant</span>
              <span className="statement-badge statement-badge-muted">Updated {lastUpdated}</span>
            </div>
          </header>

          <section className="statement-grid">
            <article className="statement-card statement-card-wide">
              <h2>Our Commitment</h2>
              <p>
                RDV Systems is committed to ensuring digital accessibility for people with
                disabilities. We are continually improving the user experience for everyone and
                applying the relevant accessibility standards to provide equal access across the
                platform.
              </p>
            </article>

            <article className="statement-card">
              <h2>Conformance Status</h2>
              <p>
                The Web Content Accessibility Guidelines (WCAG) define three levels of
                conformance: Level A, Level AA, and Level AAA. 123bimlive.com is partially conformant
                with WCAG 2.1 level AA, which means some parts of the content do not yet fully
                conform to the accessibility standard.
              </p>
            </article>

            <article className="statement-card">
              <h2>Accessibility Features</h2>
              <ul>
                {accessibilityFeatures.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </article>

            <article className="statement-card">
              <h2>Known Limitations</h2>
              <p>
                Despite ongoing work, there may still be areas that need improvement. We are
                actively prioritizing the following issues:
              </p>
              <ul>
                {knownLimitations.map((limitation) => (
                  <li key={limitation}>{limitation}</li>
                ))}
              </ul>
            </article>

            <article className="statement-card">
              <h2>Feedback and Support</h2>
              <p>
                We welcome feedback on the accessibility of 123bimlive.com. If you encounter a
                barrier, contact us through one of the channels below:
              </p>
              <ul>
                <li>
                  Email:{' '}
                  <a href="mailto:accessibility@rdvsystems.com">accessibility@rdvsystems.com</a>
                </li>
                {supportChannels.map((channel) => (
                  <li key={channel}>{channel}</li>
                ))}
              </ul>
            </article>

            <article className="statement-card">
              <h2>Formal Complaints</h2>
              <p>
                If you are not satisfied with our response to an accessibility concern, you may
                file a formal complaint with the relevant authority in your jurisdiction.
              </p>
            </article>

            <article className="statement-card">
              <h2>Technical Specifications</h2>
              <p>
                Accessibility of 123bimlive.com relies on the following technologies to work with the
                combination of your web browser and assistive technologies or plugins installed on
                your device:
              </p>
              <ul>
                {technicalSpecifications.map((specification) => (
                  <li key={specification}>{specification}</li>
                ))}
              </ul>
            </article>

            <article className="statement-card">
              <h2>Assessment Approach</h2>
              <p>RDV Systems assesses the accessibility of the platform using the following methods:</p>
              <ul>
                {assessmentApproach.map((approach) => (
                  <li key={approach}>{approach}</li>
                ))}
              </ul>
            </article>

            <article className="statement-card statement-card-wide">
              <h2>Updates to This Statement</h2>
              <p>
                This accessibility statement is reviewed and updated regularly to reflect changes
                to the website, platform capabilities, and accessibility practices. We encourage
                users to revisit this page periodically for the latest information.
              </p>
            </article>
          </section>

          <footer className="statement-footer">
            <p>
              This statement was created using the{' '}
              <a
                href="https://www.w3.org/WAI/planning/statements/"
                target="_blank"
                rel="noopener noreferrer"
              >
                W3C Accessibility Statement Generator Tool
              </a>
              .
            </p>
          </footer>
        </div>
      </div>
    </main>
  );
};

export default AccessibilityStatement;
